import React from 'react';
import { type FunnelNavigate } from '@bbkFunnel/events';
import {
  type Discount,
  type Subscription,
} from '@bbkPricingPage/types/pricing-table-types';

export type FunnelContextProps = Partial<{
  // whether we render yellow bars on top of the cards
  isDesigner: boolean;
  // void all onClicks in the Funnel, also used by pricing page editor/preview for context aware checkouts
  isPreview: boolean;
  isFullPreview: boolean;
  isBrandingPage: boolean;
  isHostedPage: boolean;
  isPricingPage: boolean;
  /**
   * How a `link` sync performs its top-level navigation. Defaults to this
   * window; in-app runs hand the URL to the merchant page instead.
   */
  navigate: FunnelNavigate;
  funnelProps: {
    funnelId: `${number}` | number;
  };
  pricingPageProps: {
    pricingPageId: string;
    pricingPageEVId?: number;
    host?: string;
    pricingPageKey?: string;
    appId: string;
    siteId: string;
    /** Atomic Pricing site id from /sites/{siteId}/pricing/{pageId} render routes */
    legacySiteId?: string;
    /** Atomic Pricing page id from /sites/{siteId}/pricing/{pageId} render routes */
    legacyPricingPageId?: string;
    bbkAuthenticityToken?: string;
    sessionId?: string;
    customerId?: string;
    discounts?: Discount[];
    subscription?: Subscription;
    isDirty?: boolean;
    publishState?: 'draft' | 'live';
    defaultCurrency?: string;
    priceVariantId?: string;
  };
}>;

export const FunnelContext = React.createContext<FunnelContextProps>({});

/**
 * Whether the funnel is being rendered for an admin rather than a customer.
 *
 * Cannot be read from any single flag: the designer, the editors' previews and
 * the branding page each mark themselves differently, while the live funnel and
 * the in-app lifecycle run set none of them. An absent flag therefore has to
 * mean customer-facing, which is why every admin flag is listed here.
 */
export const isAdminFunnelSurface = (context: FunnelContextProps): boolean =>
  Boolean(
    context.isPreview ||
    context.isDesigner ||
    context.isFullPreview ||
    context.isBrandingPage
  );
